import { Article } from "@modules/article/entities/articleEntity"
import { useServicesContext } from "@modules/core/services/services.context"
import { useInfiniteQuery } from "@tanstack/react-query"
import { useMemo } from "react"

export const useArticle = (articleId?: string) => {
  const { articleApi } = useServicesContext()

  const { data, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ["articles"],
    staleTime: 1000 * 60 * 60 * 24,
    queryFn: async ({ pageParam }) => {
      const result = await articleApi.getArticles({ page: pageParam })

      return {
        ...result,
        content: result.content.map((articleDto) => new Article(articleDto)),
        lastPage: !result.hasMore,
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.content.length === 0) {
        return null
      }

      return lastPage.page + 1
    },
  })

  const articleList = useMemo(() => data?.pages.flatMap((page) => page.content), [data])

  const articlesById = useMemo(
    () =>
      articleList?.reduce<Record<string, Article>>((acc, article) => {
        return {
          ...acc,
          [article._id]: article,
        }
      }, {}),
    [articleList],
  )

  const article = useMemo(() => (articleId ? articlesById?.[articleId] : undefined), [articlesById, articleId])

  return {
    articleList,
    article,
    fetchNextArticles: fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  }
}
